import { defineSlotRecipe } from '@chakra-ui/react';

// v2 Modal parts → v3 Dialog slots. Dropped from v2 without replacement:
// - dialogContainer flex/centering + isCentered branching — v3's placement
//   variant axis (defaulted to center below, matching v2 isCentered: true)
// - scrollBehavior branching — v3's own scrollBehavior axis
// - mode(light, dark) collapsed to the light value

/** v2 sized body/footer padding alongside maxW; smaller sizes share values */
const sized = (maxW: string, pads?: { body: object; footer: object }) => ({
  content: { maxW },
  ...(pads ?? {}),
});

const modalSlotRecipe = defineSlotRecipe({
  slots: [
    'positioner',
    'backdrop',
    'content',
    'header',
    'closeTrigger',
    'body',
    'footer',
  ],
  base: {
    positioner: {
      display: 'flex',
      width: '100vw',
      height: '100dvh',
      position: 'fixed',
      inset: 0,
      zIndex: 'modal',
      justifyContent: 'center',
      alignItems: 'center',
      overflowY: 'auto',
    },
    backdrop: {
      bg: 'blackAlpha.600',
      zIndex: 'modal',
    },
    content: {
      borderRadius: 'lg',
      bg: 'white',
      color: 'inherit',
      mx: 4,
      zIndex: 'modal',
      boxShadow: 'xl',
    },
    header: {
      px: 6,
      py: 4,
      minHeight: 20,
      fontSize: 'xl',
      display: 'flex',
      alignItems: 'center',
      fontWeight: 'bold',
      bg: 'primary.900',
      color: 'white',
      userSelect: 'none',
    },
    closeTrigger: {
      position: 'absolute',
      top: 2,
      right: 3,
      // v2's ModalCloseButton was a CloseButton with its own theme (Chakra 2.x
      // close-button, size md). v3's CloseButton is a ghost IconButton, so it
      // took the portal button recipe: an 80×40 pill, a blue.200 ring on any
      // focus (Zag focuses it on open) and a blue.50 hover. These slot styles
      // reach it through Dialog.CloseTrigger asChild and are emitted after the
      // button recipe in the same layer, so they win.
      w: 8,
      h: 8,
      minW: 8,
      borderRadius: 'md',
      _hover: { bg: 'blackAlpha.100' },
      _active: { bg: 'blackAlpha.200' },
      _focus: { boxShadow: 'none' },
      _focusVisible: { boxShadow: 'outline', outline: 'none' },
    },
    body: {
      px: [6, null, 20],
      py: 10,
      flex: 1,
    },
    footer: {
      px: [6, null, 20],
      pt: 5,
      pb: 16,
    },
  },
  variants: {
    size: {
      xs: sized('xs', {
        body: { px: 8, py: 6, pb: 8 },
        footer: { px: 8, pb: 8 },
      }),
      sm: sized('sm', {
        body: { px: 8, py: 6, pb: 8 },
        footer: { px: 8, pb: 8 },
      }),
      md: sized('md', {
        body: { px: 8, py: 6, pb: 8 },
        footer: { px: 8, pb: 8 },
      }),
      lg: sized('lg', {
        body: { px: 10, py: 8, pb: 10 },
        footer: { px: 10, pb: 10 },
      }),
      xl: sized('xl', {
        body: { px: 12, py: 8, pb: 10 },
        footer: { px: 12, pb: 10 },
      }),
      '2xl': sized('2xl'),
      '3xl': sized('3xl'),
      '4xl': sized('4xl'),
      '5xl': sized('5xl'),
      '6xl': sized('6xl'),
      full: { content: { maxW: '100vw', h: '100vh' } },
    },
    variant: {
      warning: {
        header: {
          bg: 'warning.300',
          color: 'black',
        },
      },
    },
  },
  defaultVariants: {
    size: 'lg',
    // v2 defaultProps isCentered: true
    placement: 'center',
  } as never,
});

export default modalSlotRecipe;
